import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI, User, Position } from '../api/api';
import { useAuth } from '../context/AuthContext';
import Navbar from './Navbar';
import './AdminPanel.css';
import './Modal.css';

const AdminPanel: React.FC = () => {
  const navigate = useNavigate();
  const { impersonateUser, user: currentUser } = useAuth();
  const [pendingUsers, setPendingUsers] = useState<User[]>([]);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'pending' | 'users' | 'positions' | 'stats'>('pending');
  const [loading, setLoading] = useState(true);
  const [positions, setPositions] = useState<Position[]>([]);
  const [newPositionName, setNewPositionName] = useState('');
  const [positionModalUserId, setPositionModalUserId] = useState<number | null>(null);
  const [selectedPositionId, setSelectedPositionId] = useState<number | null>(null);
  const [showCreatePositionModal, setShowCreatePositionModal] = useState(false);
  const [usersSearchQuery, setUsersSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 10;
  const [positionsSearchQuery, setPositionsSearchQuery] = useState('');
  const [currentPositionsPage, setCurrentPositionsPage] = useState(1);
  const positionsPerPage = 10;
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [newUserUsername, setNewUserUsername] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserPositionId, setNewUserPositionId] = useState<number | null>(null);
  const [newUserIsManager, setNewUserIsManager] = useState(false);
  const [newUserRole, setNewUserRole] = useState('user');
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editUserUsername, setEditUserUsername] = useState('');
  const [editUserEmail, setEditUserEmail] = useState('');
  const [editUserPassword, setEditUserPassword] = useState('');
  const [editUserPositionId, setEditUserPositionId] = useState<number | null>(null);
  const [editUserIsManager, setEditUserIsManager] = useState(false);
  const [editUserRole, setEditUserRole] = useState('user');

  const { user, isImpersonating } = useAuth();

  useEffect(() => {
    loadData();
  }, [user?.id, isImpersonating]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [pending, users, statistics, positionsList] = await Promise.all([
        adminAPI.getPendingUsers(),
        adminAPI.getAllUsers(),
        adminAPI.getStats(),
        adminAPI.getPositions(),
      ]);
      setPendingUsers(pending);
      setAllUsers(users);
      setStats(statistics);
      setPositions(positionsList);
    } catch (error: any) {
      console.error('Ошибка при загрузке данных:', error);
      if (error.response?.status === 403) {
        alert('У вас нет прав администратора');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId: number) => {
    try {
      await adminAPI.approveUser(userId);
      await loadData();
    } catch (error) {
      console.error('Ошибка при подтверждении пользователя:', error);
    }
  };

  const handleReject = async (userId: number) => {
    if (!window.confirm('Вы уверены, что хотите отклонить этого пользователя?')) {
      return;
    }
    try {
      await adminAPI.deleteUser(userId);
      await loadData();
    } catch (error) {
      console.error('Ошибка при отклонении пользователя:', error);
    }
  };

  const handleDeleteUser = async (userId: number) => {
    if (!window.confirm('Вы уверены, что хотите удалить этого пользователя? Данные будут сохранены в архиве и могут быть восстановлены.')) {
      return;
    }
    try {
      await adminAPI.deleteUser(userId);
      await loadData();
    } catch (error: any) {
      console.error('Ошибка при удалении пользователя:', error);
      alert(error.response?.data?.error || 'Не удалось удалить пользователя');
    }
  };

  const handleRoleChange = async (userId: number, newRole: string) => {
    // Внутренние роли больше не отображаются в UI, но оставляем функцию на случай,
    // если когда-нибудь понадобится менять role напрямую.
    try {
      await adminAPI.changeUserRole(userId, newRole);
      await loadData();
    } catch (error) {
      console.error('Ошибка при изменении роли:', error);
    }
  };

  const handleManagerToggle = async (userId: number, isManager: boolean) => {
    try {
      await adminAPI.setManager(userId, isManager);
      await loadData();
    } catch (error) {
      console.error('Ошибка при изменении руководящей должности:', error);
    }
  };

  const handlePositionChange = async (userId: number, position: string) => {
    try {
      await adminAPI.setUserPosition(userId, position.trim() === '' ? null : position);
      await loadData();
    } catch (error) {
      console.error('Ошибка при изменении должности:', error);
    }
  };

  const openPositionModal = (userId: number) => {
    setPositionModalUserId(userId);
    setSelectedPositionId(null);
  };

  const closePositionModal = () => {
    setPositionModalUserId(null);
    setSelectedPositionId(null);
  };

  const handleAssignPositionToUser = async () => {
    if (!positionModalUserId || !selectedPositionId) return;
    try {
      await adminAPI.assignUserPosition(positionModalUserId, selectedPositionId);
      await loadData();
      closePositionModal();
    } catch (error) {
      console.error('Ошибка при назначении должности пользователю:', error);
      alert('Не удалось назначить должность');
    }
  };

  const handleCreatePosition = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPositionName.trim()) {
      return;
    }
    try {
      const created = await adminAPI.createPosition(newPositionName.trim(), false);
      setPositions((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
      setNewPositionName('');
      setShowCreatePositionModal(false);
    } catch (error) {
      console.error('Ошибка при создании должности:', error);
      alert('Не удалось создать должность');
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newUserUsername.trim() || !newUserEmail.trim() || !newUserPassword.trim()) {
      alert('Заполните все обязательные поля');
      return;
    }
    try {
      await adminAPI.createUser(
        newUserUsername.trim(),
        newUserEmail.trim(),
        newUserPassword,
        newUserPositionId || undefined,
        newUserIsManager,
        newUserRole
      );
      // Очищаем форму
      setNewUserUsername('');
      setNewUserEmail('');
      setNewUserPassword('');
      setNewUserPositionId(null);
      setNewUserIsManager(false);
      setNewUserRole('user');
      setShowCreateUserModal(false);
      // Обновляем список пользователей
      await loadData();
    } catch (error: any) {
      console.error('Ошибка при создании пользователя:', error);
      alert(error.response?.data?.error || 'Не удалось создать пользователя');
    }
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setEditUserUsername(user.username);
    setEditUserEmail(user.email);
    setEditUserPassword('');
    setEditUserPositionId(null);
    setEditUserIsManager(user.is_manager || false);
    setEditUserRole(user.role || 'user');
    
    // Find position ID by name
    if (user.position) {
      const position = positions.find(p => p.name === user.position);
      if (position) {
        setEditUserPositionId(position.id);
      }
    }
    
    setShowEditUserModal(true);
  };

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;
    
    if (!editUserUsername.trim() || !editUserEmail.trim()) {
      alert('Имя пользователя и email обязательны');
      return;
    }
    
    try {
      await adminAPI.updateUser(
        editingUser.id,
        editUserUsername.trim(),
        editUserEmail.trim(),
        editUserPassword.trim() || undefined,
        editUserPositionId || undefined,
        editUserIsManager,
        editUserRole
      );
      
      setShowEditUserModal(false);
      setEditingUser(null);
      await loadData();
    } catch (error: any) {
      console.error('Ошибка при обновлении пользователя:', error);
      alert(error.response?.data?.error || 'Не удалось обновить пользователя');
    }
  };

  const handleUpdatePosition = async (position: Position) => {
    try {
      await adminAPI.updatePosition(position.id, position.name, position.is_manager);
      setPositions((prev) =>
        prev.map((p) => (p.id === position.id ? position : p)).sort((a, b) => a.name.localeCompare(b.name))
      );
    } catch (error) {
      console.error('Ошибка при обновлении должности:', error);
      alert('Не удалось обновить должность');
    }
  };

  const handleDeletePosition = async (id: number) => {
    if (!window.confirm('Удалить эту должность?')) return;
    try {
      await adminAPI.deletePosition(id);
      setPositions((prev) => prev.filter((p) => p.id !== id));
    } catch (error) {
      console.error('Ошибка при удалении должности:', error);
      alert('Не удалось удалить должность');
    }
  };

  const handleImpersonate = async (userId: number) => {
    if (!window.confirm('Войти под этим пользователем? Вы сможете вернуться к своему аккаунту администратора.')) {
      return;
    }
    try {
      await impersonateUser(userId);
      navigate('/boards');
    } catch (error: any) {
      console.error('Ошибка при входе под пользователем:', error);
      alert(error.response?.data?.error || 'Не удалось войти под пользователем');
    }
  };

  // Filter users by search query
  const filteredUsers = allUsers.filter(user => {
    const query = usersSearchQuery.toLowerCase();
    return (
      user.username.toLowerCase().includes(query) ||
      (user.email && user.email.toLowerCase().includes(query)) ||
      (user.position && user.position.toLowerCase().includes(query))
    );
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);
  const startIndex = (currentPage - 1) * usersPerPage;
  const endIndex = startIndex + usersPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

  // Reset to page 1 when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [usersSearchQuery]);

  // Filter positions by search query
  const filteredPositions = positions.filter(position => {
    const query = positionsSearchQuery.toLowerCase();
    return position.name.toLowerCase().includes(query);
  });

  // Pagination calculations for positions
  const totalPositionsPages = Math.ceil(filteredPositions.length / positionsPerPage);
  const positionsStartIndex = (currentPositionsPage - 1) * positionsPerPage;
  const positionsEndIndex = positionsStartIndex + positionsPerPage;
  const paginatedPositions = filteredPositions.slice(positionsStartIndex, positionsEndIndex);

  // Reset to page 1 when positions search query changes
  useEffect(() => {
    setCurrentPositionsPage(1);
  }, [positionsSearchQuery]);

  // Adjust current page if it becomes empty after deletion (for users)
  useEffect(() => {
    if (filteredUsers.length > 0) {
      const maxPage = Math.ceil(filteredUsers.length / usersPerPage);
      if (currentPage > maxPage) {
        setCurrentPage(Math.max(1, maxPage));
      }
    } else if (currentPage > 1) {
      setCurrentPage(1);
    }
  }, [filteredUsers.length, currentPage, usersPerPage]);

  // Adjust current page if it becomes empty after deletion (for positions)
  useEffect(() => {
    if (filteredPositions.length > 0) {
      const maxPage = Math.ceil(filteredPositions.length / positionsPerPage);
      if (currentPositionsPage > maxPage) {
        setCurrentPositionsPage(Math.max(1, maxPage));
      }
    } else if (currentPositionsPage > 1) {
      setCurrentPositionsPage(1);
    }
  }, [filteredPositions.length, currentPositionsPage, positionsPerPage]);

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="loading">Загрузка...</div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="admin-panel-container">
        <h1>Панель администратора</h1>
        
        <div className="admin-tabs">
          <button
            className={activeTab === 'pending' ? 'active' : ''}
            onClick={() => setActiveTab('pending')}
          >
            Ожидающие подтверждения ({pendingUsers.length})
          </button>
          <button
            className={activeTab === 'users' ? 'active' : ''}
            onClick={() => setActiveTab('users')}
          >
            Все пользователи ({allUsers.length})
          </button>
          <button
            className={activeTab === 'positions' ? 'active' : ''}
            onClick={() => setActiveTab('positions')}
          >
            Должности
          </button>
          <button
            className={activeTab === 'stats' ? 'active' : ''}
            onClick={() => setActiveTab('stats')}
          >
            Статистика
          </button>
        </div>

        {activeTab === 'pending' && (
          <div className="admin-section">
            <h2>Пользователи, ожидающие подтверждения</h2>
            {pendingUsers.length === 0 ? (
              <div className="empty-state">Нет пользователей, ожидающих подтверждения</div>
            ) : (
              <div className="users-list">
                {pendingUsers.map((user) => (
                  <div key={user.id} className="user-card">
                    <div className="user-info">
                      <div className="user-name">{user.username}</div>
                      <div className="user-email">{user.email}</div>
                      <div className="user-date">
                        Зарегистрирован: {new Date(user.created_at || '').toLocaleString('ru-RU')}
                      </div>
                    </div>
                    <div className="user-actions">
                      <button
                        onClick={() => handleApprove(user.id)}
                        className="approve-btn"
                      >
                        Одобрить
                      </button>
                      <button
                        onClick={() => handleReject(user.id)}
                        className="reject-btn"
                      >
                        Отклонить
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'users' && (
          <div className="admin-section">
            <h2>Все пользователи</h2>
            <button
              type="button"
              className="approve-btn"
              onClick={() => setShowCreateUserModal(true)}
              style={{ marginBottom: '1rem' }}
            >
              Создать пользователя
            </button>
            <div className="users-search-container">
              <input
                type="text"
                className="users-search-input"
                placeholder="Поиск по имени, email или должности..."
                value={usersSearchQuery}
                onChange={(e) => setUsersSearchQuery(e.target.value)}
              />
            </div>
            <div className="users-list">
              {paginatedUsers.length === 0 ? (
                <div className="empty-state">
                  {usersSearchQuery ? 'Ничего не найдено' : 'Нет пользователей'}
                </div>
              ) : (
                paginatedUsers.map((user) => (
                <div key={user.id} className="user-card">
                  <div className="user-info">
                    <div className="user-name">
                      {user.username}
                      {user.role === 'admin' && <span className="admin-badge">Админ</span>}
                    </div>
                    <div className="user-email">{user.email}</div>
                    <div className="user-date">
                      Зарегистрирован: {new Date(user.created_at || '').toLocaleString('ru-RU')}
                    </div>
                    <div className="user-status">
                      Статус: {user.approved ? '✅ Подтвержден' : '⏳ Ожидает подтверждения'}
                    </div>
                    <div className="user-position">
                      Должность: {user.position || 'Не указана'}
                    </div>
                    <div className="user-status">
                      Руководящая должность: {user.is_manager ? 'Да' : 'Нет'}
                    </div>
                  </div>
                  <div className="user-actions">
                    <div className="user-actions-top">
                    <button
                      type="button"
                      className="impersonate-btn"
                      onClick={() => handleImpersonate(user.id)}
                      title="Войти под этим пользователем"
                    >
                      Войти как
                    </button>
                    <button
                      type="button"
                      className="approve-btn assign-position-btn"
                      onClick={() => openPositionModal(user.id)}
                    >
                      Назначить должность
                      </button>
                    </div>
                    <button
                      type="button"
                      className="edit-user-btn user-actions-edit"
                      onClick={() => handleEditUser(user)}
                      title="Редактировать пользователя"
                    >
                      ✏️ Редактировать
                    </button>
                    <button
                      type="button"
                      className="reject-btn user-actions-delete"
                      onClick={() => handleDeleteUser(user.id)}
                      title="Удалить пользователя (данные сохранятся в архиве)"
                    >
                      Удалить
                    </button>
                  </div>
                </div>
                ))
              )}
            </div>
            {totalPages > 1 && (
              <div className="pagination">
                <button
                  className="pagination-btn"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  ← Назад
                </button>
                <span className="pagination-info">
                  Страница {currentPage} из {totalPages} ({filteredUsers.length} пользователей)
                </span>
                <button
                  className="pagination-btn"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                >
                  Вперед →
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'positions' && (
          <div className="admin-section">
            <h2>Должности</h2>

            <button
              type="button"
              className="approve-btn"
              onClick={() => setShowCreatePositionModal(true)}
              style={{ marginBottom: '1rem' }}
            >
              Добавить должность
            </button>

            <div className="users-search-container">
              <input
                type="text"
                className="users-search-input"
                placeholder="Поиск по названию должности..."
                value={positionsSearchQuery}
                onChange={(e) => setPositionsSearchQuery(e.target.value)}
              />
            </div>

            <div className="users-list">
              {paginatedPositions.length === 0 ? (
                <div className="empty-state">
                  {positionsSearchQuery ? 'Ничего не найдено' : 'Должности еще не созданы'}
                </div>
              ) : (
                paginatedPositions.map((position) => (
                  <div key={position.id} className="user-card">
                    <div className="user-info">
                      <div className="user-name">Должность</div>
                      <div className="user-position">
                        <input
                          type="text"
                          className="position-input"
                          value={position.name}
                          onChange={(e) =>
                            setPositions((prev) =>
                              prev.map((p) =>
                                p.id === position.id ? { ...p, name: e.target.value } : p
                              )
                            )
                          }
                          onBlur={() => handleUpdatePosition(position)}
                        />
                      </div>
                    </div>
                    <div className="user-actions">
                      <div className="user-actions-row">
                        <button
                          type="button"
                          className="reject-btn"
                          onClick={() => handleDeletePosition(position.id)}
                        >
                          Удалить
                        </button>
                        <label className="manager-toggle">
                          <input
                            type="checkbox"
                            checked={position.is_manager}
                            onChange={(e) =>
                              setPositions((prev) =>
                                prev.map((p) =>
                                  p.id === position.id ? { ...p, is_manager: e.target.checked } : p
                                )
                              )
                            }
                            onBlur={() => handleUpdatePosition(position)}
                          />
                          <span>Руководящая должность</span>
                        </label>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
            {totalPositionsPages > 1 && (
              <div className="pagination">
                <button
                  className="pagination-btn"
                  onClick={() => setCurrentPositionsPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPositionsPage === 1}
                >
                  ← Назад
                </button>
                <span className="pagination-info">
                  Страница {currentPositionsPage} из {totalPositionsPages} ({filteredPositions.length} должностей)
                </span>
                <button
                  className="pagination-btn"
                  onClick={() => setCurrentPositionsPage(prev => Math.min(totalPositionsPages, prev + 1))}
                  disabled={currentPositionsPage === totalPositionsPages}
                >
                  Вперед →
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'stats' && stats && (
          <div className="admin-section">
            <h2>Статистика</h2>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-value">{stats.totalUsers}</div>
                <div className="stat-label">Всего пользователей</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{stats.pendingUsers}</div>
                <div className="stat-label">Ожидают подтверждения</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{stats.totalTasks}</div>
                <div className="stat-label">Всего задач</div>
              </div>
              {stats.tasksByStatus && (
                <div className="stat-card">
                  <div className="stat-label">Задачи по статусам:</div>
                  <div className="status-stats">
                    {stats.tasksByStatus.map((item: any) => (
                      <div key={item.status} className="status-stat">
                        <span className="status-name">
                          {item.status === 'open' ? 'Открытые' : 
                           item.status === 'completed' ? 'Завершенные' : 
                           item.status === 'in_review' ? 'На проверке' : item.status}
                        </span>
                        <span className="status-count">{item.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {positionModalUserId !== null && (
          <div className="modal-overlay" onClick={closePositionModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Выбрать должность</h2>
                <button className="close-btn" onClick={closePositionModal}>
                  ×
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label>Должность</label>
                  <select
                    value={selectedPositionId || ''}
                    onChange={(e) =>
                      setSelectedPositionId(e.target.value ? parseInt(e.target.value, 10) : null)
                    }
                  >
                    <option value="">Не выбрана</option>
                    {positions.map((position) => (
                      <option key={position.id} value={position.id}>
                        {position.name} {position.is_manager ? '(руководящая)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="modal-actions">
                  <button
                    className="submit-btn"
                    onClick={handleAssignPositionToUser}
                    disabled={!selectedPositionId}
                  >
                    Назначить
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showCreatePositionModal && (
          <div className="modal-overlay" onClick={() => setShowCreatePositionModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Добавить должность</h2>
                <button className="close-btn" onClick={() => setShowCreatePositionModal(false)}>
                  ×
                </button>
              </div>
              <form onSubmit={handleCreatePosition}>
                <div className="form-group">
                  <label>Название должности</label>
                  <input
                    type="text"
                    value={newPositionName}
                    onChange={(e) => setNewPositionName(e.target.value)}
                    placeholder="Например, Программист"
                    required
                  />
                </div>
                <div className="modal-actions">
                  <button type="submit" className="submit-btn">
                    Добавить
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showCreateUserModal && (
          <div className="modal-overlay" onClick={() => setShowCreateUserModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Создать пользователя</h2>
                <button className="close-btn" onClick={() => setShowCreateUserModal(false)}>
                  ×
                </button>
              </div>
              <form onSubmit={handleCreateUser}>
                <div className="form-group">
                  <label>Имя пользователя *</label>
                  <input
                    type="text"
                    value={newUserUsername}
                    onChange={(e) => setNewUserUsername(e.target.value)}
                    placeholder="Введите имя пользователя"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={newUserEmail}
                    onChange={(e) => setNewUserEmail(e.target.value)}
                    placeholder="user@example.com"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Пароль *</label>
                  <input
                    type="password"
                    value={newUserPassword}
                    onChange={(e) => setNewUserPassword(e.target.value)}
                    placeholder="Введите пароль"
                    required
                    minLength={6}
                  />
                </div>
                <div className="form-group">
                  <label>Должность</label>
                  <select
                    value={newUserPositionId || ''}
                    onChange={(e) => setNewUserPositionId(e.target.value ? parseInt(e.target.value, 10) : null)}
                  >
                    <option value="">Не выбрана</option>
                    {positions.map((position) => (
                      <option key={position.id} value={position.id}>
                        {position.name} {position.is_manager ? '(руководящая)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Роль</label>
                  <select
                    value={newUserRole}
                    onChange={(e) => setNewUserRole(e.target.value)}
                  >
                    <option value="user">Пользователь</option>
                    <option value="admin">Администратор</option>
                  </select>
                </div>
                <div className="modal-actions">
                  <button type="submit" className="submit-btn">
                    Создать
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showEditUserModal && editingUser && (
          <div className="modal-overlay" onClick={() => setShowEditUserModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Редактировать пользователя</h2>
                <button className="close-btn" onClick={() => setShowEditUserModal(false)}>
                  ×
                </button>
              </div>
              <form onSubmit={handleUpdateUser}>
                <div className="form-group">
                  <label>Имя пользователя *</label>
                  <input
                    type="text"
                    value={editUserUsername}
                    onChange={(e) => setEditUserUsername(e.target.value)}
                    placeholder="Введите имя пользователя"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={editUserEmail}
                    onChange={(e) => setEditUserEmail(e.target.value)}
                    placeholder="user@example.com"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Пароль (оставьте пустым, чтобы не менять)</label>
                  <input
                    type="password"
                    value={editUserPassword}
                    onChange={(e) => setEditUserPassword(e.target.value)}
                    placeholder="Введите новый пароль или оставьте пустым"
                    minLength={6}
                  />
                </div>
                <div className="form-group">
                  <label>Должность</label>
                  <select
                    value={editUserPositionId || ''}
                    onChange={(e) => setEditUserPositionId(e.target.value ? parseInt(e.target.value, 10) : null)}
                  >
                    <option value="">Не выбрана</option>
                    {positions.map((position) => (
                      <option key={position.id} value={position.id}>
                        {position.name} {position.is_manager ? '(руководящая)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Роль</label>
                  <select
                    value={editUserRole}
                    onChange={(e) => setEditUserRole(e.target.value)}
                  >
                    <option value="user">Пользователь</option>
                    <option value="admin">Администратор</option>
                  </select>
                </div>
                <div className="modal-actions">
                  <button type="submit" className="submit-btn">
                    Сохранить
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AdminPanel;


